'use client';
import React, { useState, useEffect, useCallback } from 'react';
import TechnicianHeader from './TechnicianHeader';
import JobQueue from './JobQueue';
import LiveSalaryPanel from './LiveSalaryPanel';
import CashAdvanceModal from './CashAdvanceModal';
import { TechJob, PayrollCutoff } from './technicianTypes';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

function getCurrentCutoffPeriod(): { period: '1-15' | '16-31'; month: string; year: number } {
  const now = new Date();
  const day = now.getDate();
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  return {
    period: day <= 15 ? '1-15' : '16-31',
    month: months[now.getMonth()],
    year: now.getFullYear(),
  };
}

export default function TechnicianFieldContent() {
  const [jobs, setJobs] = useState<TechJob[]>([]);
  const [cutoff, setCutoff] = useState<PayrollCutoff>({
    period: '1-15',
    month: 'August',
    year: 2026,
    daysWorked: 0,
    dailyRate: 0,
    totalHoursOT: 0,
    hourlyOTRate: 0,
    jobIncentives: 0,
    approvedCashAdvance: 0,
  });
  const [technicianId, setTechnicianId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [cashAdvanceOpen, setCashAdvanceOpen] = useState(false);
  const supabase = createClient();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data: techData } = await supabase
        .from('technicians')
        .select('id, daily_rate, hourly_ot_rate, job_incentive')
        .eq('user_id', user.id)
        .single();

      if (!techData) { setLoading(false); return; }
      setTechnicianId(techData.id);

      const today = new Date().toISOString().slice(0, 10);
      const { data: apptData, error: apptError } = await supabase
        .from('appointments')
        .select('*')
        .eq('assigned_technician_id', techData.id)
        .eq('scheduled_date', today)
        .order('scheduled_time', { ascending: true });

      if (apptError) { toast.error('Failed to load jobs'); }

      const mappedJobs: TechJob[] = (apptData || []).map((row) => ({
        id: row.id,
        customerNumber: row.customer_number,
        customerName: row.customer_name,
        address: row.address || '',
        landmark: row.landmark || '',
        machineModel: row.machine_model || '',
        scheduledTime: row.scheduled_time?.slice(0, 5) || '',
        paymentMode: row.payment_mode as TechJob['paymentMode'],
        filters: {
          sedi: row.filter_sedi,
          pre: row.filter_pre,
          mem: row.filter_mem,
          post: row.filter_post,
        },
        status: row.status === 'in-progress' ? 'in-progress' : row.status === 'completed' ? 'completed' : 'pending',
        actualStartTime: row.actual_start_time?.slice(0, 5),
        actualEndTime: row.actual_end_time?.slice(0, 5),
        notes: row.notes || '',
      }));

      setJobs(mappedJobs);

      const cp = getCurrentCutoffPeriod();
      const { data: payrollData } = await supabase
        .from('payroll_records')
        .select('*')
        .eq('technician_id', techData.id)
        .eq('cutoff_period', cp.period)
        .eq('cutoff_month', cp.month)
        .eq('cutoff_year', cp.year)
        .maybeSingle();

      setCutoff({
        period: cp.period,
        month: cp.month,
        year: cp.year,
        daysWorked: payrollData?.days_worked || 0,
        dailyRate: Number(techData.daily_rate),
        totalHoursOT: Number(payrollData?.total_hours_ot || 0),
        hourlyOTRate: Number(techData.hourly_ot_rate),
        jobIncentives: Number(payrollData?.job_incentives || 0),
        approvedCashAdvance: Number(payrollData?.approved_cash_advance || 0),
      });
    } catch (err) {
      toast.error('Failed to load field data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time: subscribe to job status changes for this technician's today jobs
  useEffect(() => {
    if (!technicianId) return;

    const today = new Date().toISOString().slice(0, 10);
    const channel = supabase
      .channel(`technician-jobs-realtime-${technicianId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'appointments',
          filter: `assigned_technician_id=eq.${technicianId}`,
        },
        (payload) => {
          const row = payload.new as any;
          // Only update if it's today's job
          if (row.scheduled_date !== today) return;
          setJobs((prev) =>
            prev.map((j) =>
              j.id === row.id
                ? {
                    ...j,
                    status: row.status === 'in-progress' ? 'in-progress' : row.status === 'completed' ? 'completed' : 'pending',
                    paymentMode: row.payment_mode as TechJob['paymentMode'],
                    filters: {
                      sedi: row.filter_sedi,
                      pre: row.filter_pre,
                      mem: row.filter_mem,
                      post: row.filter_post,
                    },
                    actualStartTime: row.actual_start_time?.slice(0, 5),
                    actualEndTime: row.actual_end_time?.slice(0, 5),
                  }
                : j
            )
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'appointments',
          filter: `assigned_technician_id=eq.${technicianId}`,
        },
        (payload) => {
          const row = payload.new as any;
          if (row.scheduled_date !== today) return;
          // New job assigned — add to queue
          const newJob: TechJob = {
            id: row.id,
            customerNumber: row.customer_number,
            customerName: row.customer_name,
            address: row.address || '',
            landmark: row.landmark || '',
            machineModel: row.machine_model || '',
            scheduledTime: row.scheduled_time?.slice(0, 5) || '',
            paymentMode: row.payment_mode as TechJob['paymentMode'],
            filters: {
              sedi: row.filter_sedi,
              pre: row.filter_pre,
              mem: row.filter_mem,
              post: row.filter_post,
            },
            status: 'pending',
            notes: row.notes || '',
          };
          setJobs((prev) => {
            if (prev.find((j) => j.id === newJob.id)) return prev;
            toast.success(`New job assigned: ${newJob.customerName}`);
            return [...prev, newJob].sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [technicianId]);

  async function handleCompleteJob(jobId: string, data: {
    paymentMode: TechJob['paymentMode'];
    filters: TechJob['filters'];
    actualStartTime: string;
    actualEndTime: string;
  }) {
    const { error } = await supabase
      .from('appointments')
      .update({
        status: 'completed',
        payment_mode: data.paymentMode,
        filter_sedi: data.filters.sedi,
        filter_pre: data.filters.pre,
        filter_mem: data.filters.mem,
        filter_post: data.filters.post,
        actual_start_time: data.actualStartTime,
        actual_end_time: data.actualEndTime,
      })
      .eq('id', jobId);

    if (error) {
      toast.error('Failed to complete job: ' + error.message);
      return;
    }

    const filtersToDeduct = Object.entries(data.filters)
      .filter(([, used]) => used)
      .map(([key]) => key);

    for (const filterType of filtersToDeduct) {
      await supabase.rpc('decrement_filter_stock', { p_filter_type: filterType }).catch(() => {
        supabase.from('filter_inventory')
          .select('stock_quantity')
          .eq('filter_type', filterType)
          .single()
          .then(({ data: inv }) => {
            if (inv) {
              supabase.from('filter_inventory')
                .update({ stock_quantity: Math.max(0, inv.stock_quantity - 1), updated_at: new Date().toISOString() })
                .eq('filter_type', filterType);
            }
          });
      });
    }

    setJobs((prev) =>
      prev.map((j) =>
        j.id === jobId ? { ...j, status: 'completed', ...data } : j
      )
    );
    toast.success('Job completed successfully');
  }

  async function handleStartJob(jobId: string) {
    const { error } = await supabase
      .from('appointments')
      .update({ status: 'in-progress' })
      .eq('id', jobId);

    if (error) {
      toast.error('Failed to update job status');
      return;
    }
    setJobs((prev) =>
      prev.map((j) => j.id === jobId ? { ...j, status: 'in-progress' } : j)
    );
  }

  const completedToday = jobs.filter((j) => j.status === 'completed').length;
  const pendingJobs = jobs.filter((j) => j.status !== 'completed').length;

  if (loading) {
    return (
      <div className="px-4 py-5 max-w-screen-2xl mx-auto">
        <div className="bg-card rounded-xl border border-border p-8 text-center text-sm text-muted-foreground">
          Loading your job queue...
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-5 max-w-screen-2xl mx-auto space-y-5">
      <TechnicianHeader
        completedToday={completedToday}
        pendingJobs={pendingJobs}
        totalJobs={jobs.length}
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2">
          <JobQueue
            jobs={jobs}
            onStartJob={handleStartJob}
            onCompleteJob={handleCompleteJob}
          />
        </div>
        <div>
          <LiveSalaryPanel
            cutoff={cutoff}
            completedJobs={completedToday}
            onRequestAdvance={() => setCashAdvanceOpen(true)}
          />
        </div>
      </div>

      <CashAdvanceModal
        open={cashAdvanceOpen}
        onClose={() => setCashAdvanceOpen(false)}
        cutoff={cutoff}
        completedJobs={completedToday}
        technicianId={technicianId}
        onSubmitted={fetchData}
      />
    </div>
  );
}